declare global {
  interface Window {
    AppMeasurement?: Function | undefined;
  }
}

/**
 * Custom event dispatched when satellite.track is called.
 * Allows the interceptor to listen for satellite calls rather than directly patching track.
 */
interface SatelliteTrackedEvent extends CustomEvent {
  detail: {
    satelliteEventName: string | undefined;
    satelliteEventData: unknown;
    originalArgs: unknown[];
  };
}

const SATELLITE_TRACKED_EVENT = "lll-satellite-tracked";

import { logger } from "../debug";

export function mockSatellite(dataLayerName = "dataLayer") {
  logger.debugWithScope("mock-satellite", "Initializing mockSatellite", {
    dataLayerName,
  });

  let satelliteQueue: unknown[][] = [];
  let pageBottomCalled = false;
  let currentFn: typeof window._satellite.track | null = null;
  let latestPatchId = 0;

  const satelliteMockTrack = (...args: unknown[]) => satelliteQueue.push(args);

  function dispatchSatelliteEvent(
    satelliteEventName: string | undefined,
    satelliteEventData: unknown,
    originalArgs: unknown[]
  ) {
    logger.debugWithScope(
      "mock-satellite",
      `Dispatching satellite event: ${satelliteEventName}`,
      {
        data: satelliteEventData,
        args: originalArgs,
      }
    );

    const event = new CustomEvent(SATELLITE_TRACKED_EVENT, {
      detail: {
        satelliteEventName,
        satelliteEventData,
        originalArgs,
      },
    });
    document.dispatchEvent(event);
  }

  function satellitePatch(method?: typeof window._satellite.track) {
    const patchId = ++latestPatchId;
    const satelliteTrack =
      method ?? window._satellite?.track ?? satelliteMockTrack;
    return function (...args: unknown[]) {
      // Only dispatch and push to dataLayer if this is the latest patch
      // Older/nested patches just pass through to avoid duplication
      if (patchId === latestPatchId) {
        const appMeasureIdx =
          typeof window?.AppMeasurement === "function"
            ? args.findIndex((arg) => arg instanceof window.AppMeasurement)
            : -1;

        if (appMeasureIdx === -1) {
          window[dataLayerName]?.push({
            event: "satellite",
            data: args,
          });
        }

        // Dispatch event for the interceptor to listen for
        const satelliteEventName = args[0] as string | undefined;
        const satelliteEventData = args[1];
        dispatchSatelliteEvent(satelliteEventName, satelliteEventData, args);
      }

      return satelliteTrack.apply(this, args);
    };
  }

  function sinkSatelliteQueue() {
    if (satelliteQueue.length > 0) {
      logger.debugWithScope(
        "mock-satellite",
        `Draining satellite queue, count: ${satelliteQueue.length}`
      );
    }
    let queueEntry = satelliteQueue.pop();
    while (queueEntry !== undefined) {
      window._satellite?.track(...queueEntry);
      queueEntry = satelliteQueue.pop();
    }

    if (pageBottomCalled) {
      window._satellite?.pageBottom();
    }
  }

  const satelliteMock = {
    get track() {
      return satelliteMockTrack;
    },
    set track(v) {
      currentFn = satellitePatch(v);
      Object.defineProperty(window._satellite, "track", {
        configurable: true,
        enumerable: true,
        set(v) {
          const err = new Error().stack;
          if (err.includes("satellite") || err.includes("launch")) {
            currentFn = satellitePatch(v);
          } else {
            currentFn = v;
          }
          return currentFn;
        },
        get() {
          return currentFn;
        },
      });
      sinkSatelliteQueue();
    },
    pageBottom: () => {
      pageBottomCalled = true;
    },
  };

  if (typeof window !== "undefined") {
    if (window["_satellite"] === undefined) {
      logger.debugWithScope(
        "mock-satellite",
        "Creating new mock _satellite object"
      );
      window._satellite = satelliteMock;
    } else {
      logger.debugWithScope(
        "mock-satellite",
        "Patching existing _satellite.track method"
      );
      window._satellite.track = satellitePatch(window._satellite.track);
    }
  }
}

export { SATELLITE_TRACKED_EVENT, type SatelliteTrackedEvent };
